import React, { useRef, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { Dialog, Icon } from '@unite-us/ui';
import ReferralAuthSpendExpandable from './ReferralAuthSpendExpandable';
import LineItem from '../../pages/builder/LineItem';
import './PreviousAuthorizationRequestsDetails.scss';
var filterByState = function filterByState(items) {
  var approvedItems = items.filter(function (item) {
    return item.state === 'approved';
  });
  var requestedItems = items.filter(function (item) {
    return item.state === 'requested';
  });
  return {
    approvedItems: approvedItems,
    requestedItems: requestedItems
  };
};
var PreviousAuthorizationRequestsDetails = function PreviousAuthorizationRequestsDetails(_ref) {
  var _capInfo$startDate;
  var authorizationSpends = _ref.authorizationSpends,
    totalApprovedSpend = _ref.totalApprovedSpend,
    totalRequestedSpend = _ref.totalRequestedSpend,
    unit = _ref.unit,
    isCostBased = _ref.isCostBased,
    capInfo = _ref.capInfo,
    amountAvailable = _ref.amountAvailable;
  var modalRef = useRef();
  var closeModal = useCallback(function () {
    modalRef.current.closeDialog();
  }, []);
  var openModal = useCallback(function () {
    modalRef.current.openDialog();
  }, []);
  var _filterByState = filterByState(authorizationSpends),
    approvedItems = _filterByState.approvedItems,
    requestedItems = _filterByState.requestedItems;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    "data-testid": "auth-requests-details-view-details-button",
    type: "button",
    onClick: openModal,
    className: "focus:outline-none inline-block"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "IconExternalLink",
    className: "fill-current text-action-blue"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-action-blue ml-2"
  }, "View details")), /*#__PURE__*/React.createElement(Dialog, {
    ref: modalRef,
    title: "Authorization Request Details",
    close: closeModal,
    dataTestId: "auth-requests-details-dialog",
    width: "3xl",
    titleStyles: {
      fontWeight: 600,
      color: "#525F73"
    }
  }, /*#__PURE__*/React.createElement("p", null, "This section shows the amount available for authorization, and related information based on the selected service delivery dates."), /*#__PURE__*/React.createElement("hr", {
    className: "my-3"
  }), capInfo.hasCap && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("dl", {
    className: "m-0 flex justify-between gap-2"
  }, /*#__PURE__*/React.createElement(LineItem, {
    field: "AMOUNT AVAILABLE FOR AUTHORIZATION",
    dataTestElement: "auth-requests-details-amount-available",
    valueClassName: "text-text-blue text-sm leading-5"
  }, amountAvailable)), /*#__PURE__*/React.createElement("p", {
    className: "mt-3"
  }, "The amount available for authorization is the cap minus the Amount Currently Authorized."), /*#__PURE__*/React.createElement("hr", {
    className: "my-3"
  })), /*#__PURE__*/React.createElement(ReferralAuthSpendExpandable, {
    title: "Amount Currently Authorized:",
    items: approvedItems,
    totalSpend: totalApprovedSpend,
    unit: unit,
    isAuthorized: true,
    isCostBased: isCostBased
  }), capInfo.hasCap && /*#__PURE__*/React.createElement("p", {
    className: "mt-3"
  }, "Any currently authorized services that fall within the cap period are shown here."), /*#__PURE__*/React.createElement("hr", {
    className: "my-3"
  }), capInfo.hasCap && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("dl", {
    className: "m-0 flex flex-col gap-y-3 gap-x-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex justify-between"
  }, /*#__PURE__*/React.createElement(LineItem, {
    field: "Cap",
    dataTestElement: "auth-requests-details-cap",
    fieldClassName: "text-dark-grey"
  }, capInfo.formattedCap)), ((_capInfo$startDate = capInfo.startDate) === null || _capInfo$startDate === void 0 ? void 0 : _capInfo$startDate.isValid()) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("hr", {
    className: "col-span-2"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex justify-between"
  }, /*#__PURE__*/React.createElement(LineItem, {
    field: "Cap Period",
    dataTestElement: "auth-requests-details-cap-period",
    fieldClassName: "text-dark-grey"
  }, capInfo.formattedCapPeriod)), /*#__PURE__*/React.createElement("p", null, "The cap period is based on the selected service delivery dates."))), /*#__PURE__*/React.createElement("hr", {
    className: "my-3 col-span-2"
  })), /*#__PURE__*/React.createElement(ReferralAuthSpendExpandable, {
    title: "Amount Pending Authorization:",
    items: requestedItems,
    totalSpend: totalRequestedSpend,
    unit: unit,
    isAuthorized: false,
    isCostBased: isCostBased
  }), capInfo.hasCap && /*#__PURE__*/React.createElement("p", {
    className: "my-3"
  }, "If accepted, pending requests will count towards the cap and reduce the amount available for authorization."), /*#__PURE__*/React.createElement("hr", {
    className: "my-3"
  })));
};
PreviousAuthorizationRequestsDetails.propTypes = {
  authorizationSpends: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    short_id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    spend: PropTypes.number.isRequired,
    service_starts_at: PropTypes.string.isRequired,
    service_ends_at: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired
  })).isRequired,
  totalApprovedSpend: PropTypes.string.isRequired,
  totalRequestedSpend: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  capInfo: PropTypes.shape({
    hasCap: PropTypes.bool.isRequired,
    formattedCap: PropTypes.string,
    formattedCapPeriod: PropTypes.string,
    startDate: PropTypes.object
  }).isRequired,
  amountAvailable: PropTypes.string,
  isCostBased: PropTypes.bool.isRequired
};
PreviousAuthorizationRequestsDetails.defaultProps = {
  amountAvailable: null
};
export default PreviousAuthorizationRequestsDetails;
//# sourceMappingURL=PreviousAuthorizationRequestsDetails.js.map